/**
 * Renders an error summary of all form errors in a centralized location.
 * @module elements/fields/error-summary
 */

import $ from 'jquery';
import find from 'lodash/find';
import kebabCase from 'lodash/kebabCase';
import { EVENTS as VALIDATOR_EVENTS } from './validator';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';

const { VALIDATOR_SUCCESS, VALIDATOR_ERROR, VALIDATOR_POST_ERROR } = VALIDATOR_EVENTS;

export { VALIDATOR_EVENTS };

const ERROR_SUMMARY_OPTIONS = {
  errorSummaryTitle: '',
  // TODO: See if there is a preferred value here for something more generic
  fieldGroupSelectors: ['.field-group'],
  messageDataAttrs: ['floatingErrorMessage', 'errorMessage'],
  errorPrefix: 'Error:',
  formFieldSelectors: 'input,textarea,select',
  errorContainerClassName: 'error-summary-container',
  createErrorId: id => `${Math.random().toString(36).slice(2)}-${id}`,
  scrollToFn: () => window.scrollTo(0, 0),
};

export const CLASS_NAMES = {
  TITLE: 'error-summary__title',
  SUMMARY_LINK: 'error-summary__link',
};

export const EVENTS = {
  CLEAR: 'error-summary:clear',
};

const { CLEAR } = EVENTS;

const EVENT_NS = '.error-summary';

/**
 * Responsible for creating an error summary template/HTML.
 * @param {Object} data - Data used for creating the template
 * @returns {string} Represents rendered HTML
 */
const errorSummaryTemplate = ({ errorPrefix, title, invalidFields }) => `
  <div class="error-summary">
    <div class="error-summary__content">
      <h3 class="${CLASS_NAMES.TITLE}" tabIndex="-1">${title}</h3>
      <ul class="error-summary__list">
        ${invalidFields.map(({ errorMessage, label, errorItemId }) => `
          <li class="error-summary__item">
            <span class="u-screen-reader">${errorPrefix} </span>
            <span class="icon icon--xs icon--exclamation-point">
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 15.2A7.17 7.17 0 0 1 .8 8 7.17 7.17 0 0 1 8 .8 7.17 7.17 0 0 1 15.2 8 7.17 7.17 0 0 1 8 15.2zm0-5.6c.4 0 .6-.3.6-.7l.3-4.3c0-.4-.4-.7-.8-.7s-1 .2-1 .7l.3 4.3c0 .4.2.7.6.7z"/><circle cx="8" cy="11.3" r=".9"/>
              </svg>
            </span>
            <a href="#${errorItemId}" class="${CLASS_NAMES.SUMMARY_LINK}">
              ${label ? `${label}: ` : ''}${errorMessage}
            </a>
          </li>
        `).join('')}
      </ul>
    </div>
  </div>
`;

/**
 * Responsible for adding an error container to the DOM.
 * @param {jQuery} $el - The container element
 * @param {string} className - Name of class for the prepended element
 * @returns {jQuery} The error container element
 */
const createErrorContainer = ($el, className) =>
  $(`<div class="${className}"/>`).prependTo($el);

/**
 * Responsible for clearing out the error container.
 * @param {jQuery} $errorContainer - Element containing the error summary
 * @returns {Function} Clears out the error container when called on
 */
const removeErrorSummary = $errorContainer => () => {
  $errorContainer.empty();
};

/**
 * Responsible for rendering the error summary and all the details around the
 * errors.
 * @param {string} title - Title for the error summary
 * @param {jQuery} $errorContainer - Contains the error summary
 * @param {Map} fieldErrorMap - Map of field errors
 * @param {Function} scrollToFn - Handles where to scroll to when the error
 * summary has been shown
 * @return {Function} Called on by an event
 */
const makeShowErrorSummary = (
  errorPrefix,
  title,
  $errorContainer,
  fieldErrorMap,
  scrollToFn) => (evt) => {
    removeErrorSummary($errorContainer)();
    const { detail: { invalidElements } } = evt.originalEvent;
    const invalidFields = invalidElements.reduce((acc, invalidElement) => {
      const fieldError = fieldErrorMap.get(invalidElement);
      const { validationMessage: errorMessage } = invalidElement;
      const { label } = invalidElement.dataset;

      if (fieldError) {
        return acc.concat(Object.assign({}, fieldError, { errorMessage, label }));
      }

      return acc;
    }, []);

    const markup = errorSummaryTemplate({
      errorPrefix,
      title,
      invalidFields,
    });
    $errorContainer
      .html(markup)
      .find(`.${CLASS_NAMES.TITLE}`)
      .focus();

    scrollToFn();
};

/**
 * Responsible for scrolling to a field in the DOM.
 * @param {Map} fieldErrorMap - Map of field errors
 */
const scrollToField = fieldErrorMap => (evt) => {
  evt.preventDefault();

  const targetErrorSummaryId = evt.currentTarget.getAttribute('href').substr(1);
  const fieldErrorValues = Array.from(fieldErrorMap.values());
  const { $formField, $fieldContainer } = find(
    fieldErrorValues,
    ({ errorItemId }) => errorItemId === targetErrorSummaryId,
  );

  $formField.focus();
  // This is using the defaults but more information on this can be found here:
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView#Parameters
  $fieldContainer[0].scrollIntoView({
    block: 'center',
  });
};

/**
 * Responsible for cleaning up or tearing down error summary.
 * @param {jQuery} $el = The container element
 * @param {jQuery} $errorContainer - An error container
 * @param {Function} deregister - Unregisters the plugin
 */
const destroy = ($el, $errorContainer, deregister) => {
  $el.off(EVENT_NS);
  $errorContainer.remove();
  deregister();
};

/**
 * Responsible for formatting data attributes into a proper jQuery selector.
 * @param {Array} dataAttrs - List of possible data attributes
 * @returns {string} Data attributes in the form of selectors
 */
const createDataSelectors = dataAttrs =>
  dataAttrs
    .map(attr => `[data-${kebabCase(attr)}]`)
    .join(',');

/**
 * Responsible for creating a consolidated place for a form error item,
 * containing all the necessary information needed for a form error item.
 * @param {string} formSelectors - Represents form field selectors
 * @param {Function} createErrorId - Creates a unique ID
 * @returns {Function} Called on to create a field error item
 */
const createFieldErrorItem = (formSelectors, createErrorId) => (map, fieldContainer, id) => {
  let formField = fieldContainer;
  const $fieldContainer = $(fieldContainer);
  const $innerFormField = $fieldContainer.find(formSelectors);
  if ($innerFormField.length) {
    formField = $innerFormField[0];
  }

  const $formField = $(formField);
  const errorItemId = createErrorId(id);
  const info = { errorItemId, $fieldContainer, $formField };

  // This exists so that a container with a form field supercedes a form field
  // that is the same as the container. This would occur when both the
  // container and field are seen as separate elements. Since the container
  // with the form field is the desired result, make sure that wins over when
  // a form field is the same as the container.
  if (map.has(formField) && formField === $fieldContainer[0]) {
    return map;
  }

  return map.set(formField, info);
};

/**
 * Responsible for creating a map of elements which can be used for looking up
 * errors when a form has submitted.
 * @param {jQuery} $el - Container element
 * @param {Array} fieldGroupSelectors - Potential field selectors
 * @param {Array} dataAttrs - Data attributes used for error messages
 * @param {string} formSelectors - Possible form elements that can be queried
 * @param {Function} createErrorId - Creates a unique for each field
 * @returns {Map} Contains all possible fields which may have any sort of error
 * messaging
 */
const createFieldErrorMap = ($el, fieldGroupSelectors, dataAttrs, formSelectors, createErrorId) => {
  const fieldSelectors = fieldGroupSelectors
    .concat(formSelectors, createDataSelectors(dataAttrs))
    .join(',');

  return $el
    .find(fieldSelectors)
    .toArray()
    .reduce(createFieldErrorItem(formSelectors, createErrorId), new Map());
};

/**
 * Responsible for displaying an error summary that contains error information
 * upon submitting the form.
 * @param {HTMLElement} el - Element containing form field elements
 * @param {Object} options - Configurable options
 * @param {Object} actions - Actions to be called on
 * @return {Object} Methods attached to an error summary
 */
export default function errorSummary(el, options = {}, { deregister } = noopActions) {
  const $el = $(el);
  let $errorContainer = $();
  const {
    formFieldSelectors: formSelectors,
    messageDataAttrs: dataAttrs,
    errorContainerClassName,
    createErrorId,
    errorPrefix,
    fieldGroupSelectors,
    scrollToFn,
  } = Object.assign({}, ERROR_SUMMARY_OPTIONS, options, $el.data());

  const { dataset: { errorSummaryTitle: title } } = el;

  if (title) {
    $el.data('validateDisableErrorScroll', true);
    const fieldErrorMap = createFieldErrorMap(
      $el,
      fieldGroupSelectors,
      dataAttrs,
      formSelectors,
      createErrorId,
    );

    $errorContainer = createErrorContainer($el, errorContainerClassName);

    $errorContainer
      .on('click', `.${CLASS_NAMES.SUMMARY_LINK}`, scrollToField(fieldErrorMap));

    const showErrorSummary = makeShowErrorSummary(
      errorPrefix,
      title,
      $errorContainer,
      fieldErrorMap,
      scrollToFn,
    );

    $el
      .on(CLEAR + EVENT_NS, removeErrorSummary($errorContainer))
      .on(VALIDATOR_ERROR + EVENT_NS, showErrorSummary)
      .on(VALIDATOR_POST_ERROR + EVENT_NS, showErrorSummary)
      .on(VALIDATOR_SUCCESS + EVENT_NS, removeErrorSummary($errorContainer));
  }

  return {
    destroy: () => destroy($el, $errorContainer, deregister),
  };
}

registerJQueryPlugin('errorSummary', errorSummary);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/error-summary.js